import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  ACTIVITY_LEVELS,
  DEFAULT_GOAL_RATE_PCT,
  ENERGY_SOURCE,
  GOALS,
  MAX_GOAL_RATE_PCT,
} from '@kraftwerk/shared';
import type { ActivityLevel, Goal, MacroTargets, SexFormula } from '@kraftwerk/shared';
import { db } from '../db/local.js';
import { readProfile, updateBody, updateTargets } from '../db/profile.js';
import type { BodySettings } from '../db/profile.js';
import { currentOwnerId } from '../auth.js';
import { energyOfDay } from '../db/energy.js';
import { useTimeZone, useToday } from '../hooks/useToday.js';
import { journalFor } from '../db/journal.js';
import { WeightField } from './journal/JournalDay.js';
import { useIsChild } from '../hooks/useModules.js';

/**
 * Körper und Bedarf: Angaben für den Grundumsatz, Alltag ohne Sport, Ziel.
 *
 * Darunter, was daraus folgt — als Schätzung gekennzeichnet — und der
 * Zielvorschlag, der **nur auf Tipp** übernommen wird. Die Zielwerte lassen
 * sich hier auch von Hand ändern; vorher ging das nach dem ersten Setzen nicht
 * mehr.
 */
export function EnergySetting(): React.JSX.Element | null {
  const { t, i18n } = useTranslation();
  const timeZone = useTimeZone();
  const date = useToday();
  const profile = useLiveQuery(() => readProfile(db), []);
  const energy = useLiveQuery(
    () =>
      date && timeZone ? energyOfDay(date, timeZone, db, new Date(), currentOwnerId()) : undefined,
    [date, timeZone],
  );
  const ids = { year: useId(), height: useId(), level: useId(), goal: useId(), rate: useId() };
  // Gewicht gehört hierher, nicht nur ins Tagebuch: Es ist die Angabe, die am
  // häufigsten fehlt, und gespeichert wird es trotzdem als Wiegetag von heute.
  const todayEntry = useLiveQuery(
    () => (date ? journalFor(date, db) : Promise.resolve(null)),
    [date],
    null,
  );

  // Ein Teen mit Ernährung (27.10.2026): nur die eigenen Ziele — keine
  // Körperzahlen, keine Schätzung. Die Formeln im Fachkonzept gelten für
  // Erwachsene; für Jugendliche schlägt die App deshalb nichts vor.
  const child = useIsChild();

  if (!profile) return null;
  if (child) {
    return (
      <section aria-labelledby="settings-energy" className="kw-tile flex flex-col gap-3">
        <h3 id="settings-energy" className="kw-kicker">
          {t('energy.teenHeading')}
        </h3>
        <p className="text-sm text-ink-muted">{t('energy.teenIntro')}</p>
        <TargetEditor current={profile.targets} />
        <p className="text-xs text-ink-muted">{t('energy.selfSet')}</p>
      </section>
    );
  }
  const body = profile.body;
  const save = (changes: Partial<BodySettings>) => void updateBody(changes, db);

  return (
    <section aria-labelledby="settings-energy" className="kw-tile flex flex-col gap-3">
      <h3 id="settings-energy" className="kw-kicker">
        {t('energy.heading')}
      </h3>
      <p className="text-sm text-ink-muted">{t('energy.intro')}</p>

      <div className="flex flex-wrap gap-3">
        <label htmlFor={ids.year} className="flex w-28 flex-col gap-1">
          <span className="kw-label">{t('energy.birthYear')}</span>
          <NumberInput
            id={ids.year}
            value={body.birthYear}
            onCommit={(value) => save({ birthYear: value })}
          />
        </label>
        <label htmlFor={ids.height} className="flex w-28 flex-col gap-1">
          <span className="kw-label">{t('energy.heightCm')}</span>
          <NumberInput
            id={ids.height}
            value={body.heightCm}
            onCommit={(value) => save({ heightCm: value })}
          />
        </label>
      </div>

      {date ? (
        <div className="flex flex-col gap-1">
          <WeightField date={date} value={todayEntry?.bodyWeightKg ?? null} />
          <span className="text-xs text-ink-muted">
            {profile.weightKg !== null && (todayEntry?.bodyWeightKg ?? null) === null
              ? t('energy.lastWeight', { kg: profile.weightKg.toLocaleString(i18n.language) })
              : t('energy.weightWhy')}
          </span>
        </div>
      ) : null}

      <fieldset className="flex flex-col gap-1">
        <legend className="kw-label">{t('energy.sex')}</legend>
        <div className="flex gap-2">
          {(['male', 'female'] as SexFormula[]).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={body.sexFormula === option}
              onClick={() => save({ sexFormula: option })}
              className="kw-toggle"
            >
              {t(`energy.sexOption.${option}`)}
            </button>
          ))}
        </div>
        <span className="text-xs text-ink-muted">{t('energy.sexWhy')}</span>
      </fieldset>

      <label htmlFor={ids.level} className="flex flex-col gap-1">
        <span className="kw-label">{t('energy.activity')}</span>
        <select
          id={ids.level}
          value={body.activityLevel ?? ''}
          onChange={(event) =>
            save({ activityLevel: (event.target.value || null) as ActivityLevel | null })
          }
          className="kw-field"
        >
          <option value="">{t('energy.notSet')}</option>
          {ACTIVITY_LEVELS.map((level) => (
            <option key={level} value={level}>
              {t(`energy.level.${level}`)}
            </option>
          ))}
        </select>
        <span className="text-xs text-ink-muted">{t('energy.activityWhy')}</span>
      </label>

      <div className="flex flex-wrap gap-3">
        <label htmlFor={ids.goal} className="flex min-w-40 flex-1 flex-col gap-1">
          <span className="kw-label">{t('energy.goal')}</span>
          <select
            id={ids.goal}
            value={body.goal ?? ''}
            onChange={(event) => {
              const goal = (event.target.value || null) as Goal | null;
              save({ goal, goalRatePct: goal ? DEFAULT_GOAL_RATE_PCT[goal] : null });
            }}
            className="kw-field"
          >
            <option value="">{t('energy.notSet')}</option>
            {GOALS.map((goal) => (
              <option key={goal} value={goal}>
                {t(`energy.goalOption.${goal}`)}
              </option>
            ))}
          </select>
        </label>
        {body.goal && body.goal !== 'maintain' ? (
          <label htmlFor={ids.rate} className="flex w-36 flex-col gap-1">
            <span className="kw-label">{t('energy.rate')}</span>
            <NumberInput
              id={ids.rate}
              decimal
              value={body.goalRatePct ?? DEFAULT_GOAL_RATE_PCT[body.goal]}
              onCommit={(value) =>
                save({
                  goalRatePct: value === null ? null : Math.min(value, MAX_GOAL_RATE_PCT),
                })
              }
            />
          </label>
        ) : null}
      </div>

      {energy === undefined ? null : !energy.ready ? (
        // Ausdrücklich, was fehlt — statt mit Ersatzwerten zu rechnen.
        <p className="text-sm text-warn">
          {t('energy.missing', {
            list: energy.missing.map((key) => t(`energy.missingField.${key}`)).join(', '),
          })}
        </p>
      ) : (
        <div className="flex flex-col gap-2 rounded-xl border border-line bg-surface-raised p-3 text-sm">
          <p>
            {t('energy.bmr', { kcal: energy.bmr })}
            <br />
            {t('energy.typical', { kcal: energy.typicalNeed })}
          </p>
          {energy.suggestion ? (
            <SuggestionRow
              suggestion={energy.suggestion}
              current={profile.targets}
              goal={body.goal!}
            />
          ) : (
            <p className="text-xs text-ink-muted">{t('energy.noGoal')}</p>
          )}
          <p className="text-xs text-ink-muted">
            {t('energy.estimate')} {ENERGY_SOURCE}
          </p>
        </div>
      )}

      <TargetEditor current={profile.targets} />
    </section>
  );
}

function SuggestionRow({
  suggestion,
  current,
  goal,
}: {
  suggestion: { kcal: number; proteinG: number; fatG: number; carbsG: number; deltaKcal: number };
  current: MacroTargets | null;
  goal: Goal;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [taken, setTaken] = useState(false);
  const same =
    current?.kcal === suggestion.kcal &&
    current.protein === suggestion.proteinG &&
    current.fat === suggestion.fatG &&
    current.carbs === suggestion.carbsG;

  return (
    <div className="flex flex-col gap-2">
      <p>
        <span className="font-semibold">
          {t('energy.suggestion', {
            kcal: suggestion.kcal,
            protein: suggestion.proteinG,
            carbs: suggestion.carbsG,
            fat: suggestion.fatG,
          })}
        </span>
        <br />
        <span className="text-xs text-ink-muted">
          {t(`energy.delta.${goal}`, { kcal: Math.abs(suggestion.deltaKcal) })}
        </span>
      </p>
      {same ? (
        <p className="text-xs text-good">{t('energy.isCurrent')}</p>
      ) : (
        <button
          type="button"
          onClick={() => {
            void updateTargets(
              {
                kcal: suggestion.kcal,
                protein: suggestion.proteinG,
                carbs: suggestion.carbsG,
                fat: suggestion.fatG,
              },
              db,
            ).then(setTaken);
          }}
          className="kw-btn kw-btn-small"
        >
          {t('energy.take')}
        </button>
      )}
      {taken ? (
        <p role="status" className="text-xs text-good">
          {t('energy.taken')}
        </p>
      ) : null}
    </div>
  );
}

/** Zielwerte von Hand — dieselben vier wie beim ersten Einrichten. */
function TargetEditor({ current }: { current: MacroTargets | null }): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<MacroTargets | null>(null);

  if (!open) {
    return (
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="min-w-0">
          {current
            ? t('energy.currentTargets', {
                kcal: current.kcal,
                protein: current.protein,
                carbs: current.carbs,
                fat: current.fat,
              })
            : t('energy.noTargets')}
        </span>
        <button
          type="button"
          onClick={() => {
            setValues(current ?? { kcal: 0, protein: 0, carbs: 0, fat: 0 });
            setOpen(true);
          }}
          className="kw-btn kw-btn-quiet kw-btn-small shrink-0 px-3"
        >
          {t('energy.editTargets')}
        </button>
      </div>
    );
  }

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (values) void updateTargets(values, db).then(() => setOpen(false));
      }}
    >
      <div className="flex flex-wrap gap-2">
        {(['kcal', 'protein', 'carbs', 'fat'] as const).map((key) => (
          <label key={key} className="kw-label flex w-24 flex-col gap-1.5">
            {t(`nutrition.macro.${key}`)}
            <input
              inputMode="numeric"
              value={values?.[key] ?? 0}
              onChange={(event) => {
                const parsed = Number(event.target.value.replace(',', '.'));
                if (Number.isFinite(parsed) && values) setValues({ ...values, [key]: parsed });
              }}
              className="kw-field"
            />
          </label>
        ))}
      </div>
      <div className="flex gap-2">
        <button type="submit" className="kw-btn kw-btn-small flex-1">
          {t('energy.saveTargets')}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="kw-btn kw-btn-quiet kw-btn-small flex-1"
        >
          {t('nutrition.library.cancel')}
        </button>
      </div>
    </form>
  );
}

/** Zahl eingeben, gespeichert beim Verlassen des Feldes — nicht bei jedem Tastendruck. */
function NumberInput({
  id,
  value,
  decimal,
  onCommit,
}: {
  id: string;
  value: number | null;
  decimal?: boolean;
  onCommit: (value: number | null) => void;
}): React.JSX.Element {
  const [text, setText] = useState(value === null ? '' : String(value).replace('.', ','));
  return (
    <input
      id={id}
      inputMode={decimal ? 'decimal' : 'numeric'}
      value={text}
      onChange={(event) => setText(event.target.value)}
      onBlur={() => {
        const trimmed = text.trim().replace(',', '.');
        if (trimmed === '') return onCommit(null);
        const parsed = Number(trimmed);
        if (Number.isFinite(parsed) && parsed > 0) onCommit(decimal ? parsed : Math.round(parsed));
      }}
      className="kw-field"
    />
  );
}
